import React, { useState, useEffect, useRef } from "react";
import classes from "./style.module.scss";
import { useAppHeader } from "~/app/modules/appHeader";
import usePageTitle from "~/app/hooks/usePageTitle";
import { useHistory, useLocation } from "react-router-dom";
import { route } from "~/app/helpers/route";
import { TIME_MANAGEMENT_ROUTES } from '~/timeManagement/Router';
import { useStoreActions } from "~/store";
import { useTranslation } from "react-i18next";

interface StartTimeProps {
  project?: any;
  activity?: any;
  machine?: any;
  projectId?: number;
  activityId?: number;
  machineId?: number;
  modeType?: number;
  notes?: string;
}

const StartTime: React.FC = () => {
  const history = useHistory();
  const location = useLocation();
  const { t } = useTranslation();
  const { setData, clearData } = useAppHeader();
  
  const saveTimeManagement = useStoreActions((actions: any) => actions.timeManagement.save);
  const { project, activity, machine, projectId, activityId, machineId, modeType, notes } = (location.state || {}) as StartTimeProps;

  const [currentDate] = useState(new Date());
  const [isLoading, setIsLoading] = useState(false);
  
  const getCurrentTime = () => {
    const now = new Date();
    return {
      hour: now.getHours(),
      minute: now.getMinutes(),
      second: now.getSeconds()
    };
  };
  
  const currentTime = getCurrentTime();
  const [selectedHour, setSelectedHour] = useState(currentTime.hour);
  const [selectedMinute, setSelectedMinute] = useState(currentTime.minute);
  
  const hourScrollerRef = useRef<HTMLDivElement>(null);
  const minuteScrollerRef = useRef<HTMLDivElement>(null);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  usePageTitle(t('timeManagement.selectStartTime'));

  useEffect(() => {
    setData({
      title: t('timeManagement.selectStartTime'),
      subTitle: '',
      backLink: undefined,
      isTimeManagementPage: false,
      showUser: false,
      machineId: '',
      projectId: '',
      parentFolderId: 0,
      showMachineMessages: false,
      isIssueDetailPage: false,
      notification: false,
      isAddTimeForm: false,
    });

    return () => clearData();
  }, [setData, clearData, t]);

  useEffect(() => {
    const scrollToSelected = () => {
      if (hourScrollerRef.current) {
        const selectedElement = hourScrollerRef.current.querySelector(`.${classes.timeOptionSelected}`);
        if (selectedElement) {
          selectedElement.scrollIntoView({ block: 'center', behavior: 'auto' });
        }
      }
      if (minuteScrollerRef.current) {
        const selectedElement = minuteScrollerRef.current.querySelector(`.${classes.timeOptionSelected}`);
        if (selectedElement) {
          selectedElement.scrollIntoView({ block: 'center', behavior: 'auto' });
        }
      }
    };
    
    setTimeout(scrollToSelected, 100);
  }, []);

  const formatDate = (date: Date) => {
    const days = [
      t('timeManagement.sunday'),
      t('timeManagement.monday'),
      t('timeManagement.tuesday'),
      t('timeManagement.wednesday'),
      t('timeManagement.thursday'),
      t('timeManagement.friday'),
      t('timeManagement.saturday')
    ];
    const months = [
      t('timeManagement.january'),
      t('timeManagement.february'),
      t('timeManagement.march'),
      t('timeManagement.april'),
      t('timeManagement.may'),
      t('timeManagement.june'),
      t('timeManagement.july'),
      t('timeManagement.august'),
      t('timeManagement.september'),
      t('timeManagement.october'),
      t('timeManagement.november'),
      t('timeManagement.december')
    ];
    
    const dayName = days[date.getDay()];
    const day = date.getDate();
    const month = months[date.getMonth()];
    const year = date.getFullYear();
    
    return `${dayName} ${day}. ${month} ${year}`;
  };

  const currentHour = getCurrentTime().hour;
  const currentMinuteValue = getCurrentTime().minute;

  const hours = Array.from({ length: currentHour + 1 }, (_, i) => i.toString().padStart(2, '0'));
  
  const getAvailableMinutes = () => {
    if (selectedHour === currentHour) {
      return Array.from({ length: currentMinuteValue + 1 }, (_, i) => i.toString().padStart(2, '0'));
    } else {
      return Array.from({ length: 60 }, (_, i) => i.toString().padStart(2, '0'));
    }
  };
  
  const minutes = getAvailableMinutes();

  useEffect(() => {
    const current = getCurrentTime();
    if (selectedHour === current.hour && selectedMinute > current.minute) {
      setSelectedMinute(current.minute);
    }
    
    if (minuteScrollerRef.current) {
      const selectedElement = minuteScrollerRef.current.querySelector(`.${classes.timeOptionSelected}`);
      if (selectedElement) {
        selectedElement.scrollIntoView({ block: 'center', behavior: 'smooth' });
      }
    }
  }, [selectedHour, selectedMinute]);

  const getCenteredElement = (scrollerRef: React.RefObject<HTMLDivElement>): HTMLElement | null => {
    if (!scrollerRef.current) return null;
    
    const scroller = scrollerRef.current;
    const scrollerRect = scroller.getBoundingClientRect();
    const scrollerCenter = scrollerRect.top + scrollerRect.height / 2;
    
    const options = scroller.querySelectorAll('[data-value]');
    let closestElement: HTMLElement | null = null;
    let closestDistance = Infinity;
    
    options.forEach((option) => {
      const optionRect = option.getBoundingClientRect();
      const optionCenter = optionRect.top + optionRect.height / 2;
      const distance = Math.abs(scrollerCenter - optionCenter);
      
      if (distance < closestDistance) {
        closestDistance = distance;
        closestElement = option as HTMLElement;
      }
    });
    
    return closestElement;
  };

  const handleHourScroll = () => {
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }
    
    scrollTimeoutRef.current = setTimeout(() => {
      const centeredElement = getCenteredElement(hourScrollerRef);
      if (centeredElement) {
        const value = parseInt(centeredElement.getAttribute('data-value') || '0');
        if (value !== selectedHour) {
          setSelectedHour(value);
        }
      }
    }, 100);
  };

  const handleMinuteScroll = () => {
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }
    
    scrollTimeoutRef.current = setTimeout(() => {
      const centeredElement = getCenteredElement(minuteScrollerRef);
      if (centeredElement) {
        const value = parseInt(centeredElement.getAttribute('data-value') || '0');
        if (value !== selectedMinute) {
          setSelectedMinute(value);
        }
      }
    }, 100);
  };

  const handleHourChange = (hour: number) => {
    setSelectedHour(hour);
    
    if (hourScrollerRef.current) {
      const selectedElement = hourScrollerRef.current.querySelector(`[data-value="${hour}"]`);
      if (selectedElement) {
        selectedElement.scrollIntoView({ block: 'center', behavior: 'smooth' });
      }
    }
  };

  const handleMinuteChange = (minute: number) => {
    setSelectedMinute(minute);
    
    if (minuteScrollerRef.current) {
      const selectedElement = minuteScrollerRef.current.querySelector(`[data-value="${minute}"]`);
      if (selectedElement) {
        selectedElement.scrollIntoView({ block: 'center', behavior: 'smooth' });
      }
    }
  };

  const calculateElapsedTime = () => {
    const selectedDateTime = new Date();
    selectedDateTime.setHours(selectedHour, selectedMinute, 0, 0);
    
    const currentDateTime = new Date();
    
    const diffMs = currentDateTime.getTime() - selectedDateTime.getTime();
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    const elapsedHours = Math.floor(diffMinutes / 60);
    const elapsedMinutes = diffMinutes % 60;
    
    return {
      totalMinutes: diffMinutes,
      hours: elapsedHours,
      minutes: elapsedMinutes,
      formattedElapsed: `${elapsedHours}h ${elapsedMinutes}m`
    };
  };

  const handleStartDay = async () => {
    if (!projectId) {
      return;
    }
  
    setIsLoading(true);
  
    try {
      const formattedHour = selectedHour.toString().padStart(2, '0');
      const formattedMinute = selectedMinute.toString().padStart(2, '0');
      const startTime = `${formattedHour}:${formattedMinute}:00`;
      
      const dateStr = currentDate.toISOString().split('T')[0];
      
      const current = getCurrentTime();
      const isCurrentTime = selectedHour === current.hour && selectedMinute === current.minute;
      const elapsedTime = !isCurrentTime ? calculateElapsedTime() : null;
      
      const payload = {
        project_id: projectId,
        activity_id: activityId || null,
        machine_id: machineId || null,
        mode_type: modeType,
        date: dateStr,
        time: startTime,
        status: 2,
        total_time: null,
        notes: notes || null,
        auto_pause_others: true
      };  
      const response = await saveTimeManagement(payload);
      let timeManagementId = null;
      
      if (response?.id) {
        timeManagementId = response.id;
      } else if (response?.data?.id) {
        timeManagementId = response.data.id;
      } else if (response?.data?.data?.id) {
        timeManagementId = response.data.data.id;
      }
      
      if (timeManagementId) {
        history.push({
          pathname: route(TIME_MANAGEMENT_ROUTES.activeTime),
          state: {
            timeManagementId,
            project,
            activity,
            machine,
            projectId,
            activityId,
            machineId,
            modeType,
            date: dateStr,
            startTime,
            startHour: selectedHour,
            startMinute: selectedMinute,
            isCurrentTime,
            elapsedTime: elapsedTime ? {
              hours: elapsedTime.hours,
              minutes: elapsedTime.minutes,
              totalMinutes: elapsedTime.totalMinutes,
              formatted: elapsedTime.formattedElapsed
            } : null
          }
        });
      } else {
        setIsLoading(false);
      }
    } catch (error) {
      setIsLoading(false);
    }
  };

  return (
    <div className={classes['startTimeWrapper']}>
      <div className={classes['content']}>
        <div className={classes['header']}>
          <h2 className={classes['headerTitle']}>{t('timeManagement.selectStartTimeTitle')}</h2>
          <p className={classes['headerSubtitle']}>{t('timeManagement.startTimeSubtitle')}</p>
        </div>

        <div className={classes['dateCard']}>
          <div className={classes['dateText']}>{formatDate(currentDate)}</div>
        </div>

        <div className={classes['timePickerCard']}>
          <div className={classes['timePicker']}>
            <div className={classes['timeColumn']}>
              <div 
                className={classes['timeScroller']} 
                ref={hourScrollerRef}
                onScroll={handleHourScroll}
              >
                {hours.map((hour) => (
                  <div
                    key={`hour-${hour}`}
                    data-value={parseInt(hour)}
                    data-hour={parseInt(hour)}
                    className={`${classes['timeOption']} ${parseInt(hour) === selectedHour ? classes['timeOptionSelected'] : ''}`}
                    onClick={() => handleHourChange(parseInt(hour))}
                  >
                    {hour}
                  </div>
                ))}
              </div>
            </div>
            
            <div className={classes['timeSeparator']}>:</div>
            
            <div className={classes['timeColumn']}>
              <div 
                className={classes['timeScroller']} 
                ref={minuteScrollerRef}
                onScroll={handleMinuteScroll}
              >
                {minutes.map((minute) => (
                  <div
                    key={`minute-${minute}`}
                    data-value={parseInt(minute)}
                    data-minute={parseInt(minute)}
                    className={`${classes['timeOption']} ${parseInt(minute) === selectedMinute ? classes['timeOptionSelected'] : ''}`}
                    onClick={() => handleMinuteChange(parseInt(minute))}
                  >
                    {minute}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className={classes['buttonContainer']}>
          <button 
            onClick={handleStartDay} 
            className={classes['startButton']}
            disabled={isLoading}
          >
            {isLoading ? t('timeManagement.starting') : t('timeManagement.startDay')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default StartTime;